// Baixa PARCIAL de titulo do Contas a Receber — a leitura da linha e o agrupamento do recibo.
// ([DA-654], 01/09/2026)
//
// O sistema nunca deixa um titulo meio pago: ele FECHA pelo que recebeu e o que faltou vira um
// titulo NOVO (`Saldo parcial do titulo #N`, com `valor_original` carregando o valor do pai). Isso
// e' correto no banco e ilegivel na tela — o Ruan pagou R$ 66,00 de um titulo de R$ 139,00 e a aba
// Pagas imprimia `R$ 139,00 · Pago`, que le como "recebi 139 desse cupom".
//
// Peca UNICA porque a lista e o recibo fazem a MESMA leitura: duas copias divergiriam no primeiro
// ajuste, e a divergencia entre dois lugares que mostram o mesmo dado nao tem quem a delate
// (o telefone da [DA-591] passou meses assim).

/** O minimo que estas funcoes precisam ler de um titulo. Serve pra linha da lista e pro recibo. */
export interface TituloBaixavel {
  /** Valor DESTE titulo (o saldo, quando ele nasceu de uma baixa parcial). */
  valor: string | number;
  /** Quanto entrou de fato. `null` enquanto pendente. */
  valor_pago?: string | number | null;
  /** Valor do titulo que deu origem a este. Igual a `valor` quando o titulo nasceu inteiro. */
  valor_original?: string | number | null;
  status: string;
}

const num = (v: string | number | null | undefined): number => Number(v ?? 0) || 0;

/** Dinheiro se compara em centavos INTEIROS — nunca com epsilon abaixo da moeda (regra da casa). */
const centavos = (v: string | number | null | undefined): number => Math.round(num(v) * 100);

/**
 * O titulo foi quitado recebendo MENOS que o proprio valor.
 *
 * ⚠️ Medido em prod (01/09/2026): 13 titulos em 367 pagos. **7 geraram saldo** (o caso vivo) e
 * **6 sao de abril, anteriores ao cutover de 09/05** — vieram do ControleFC com diferenca de
 * centavos (`R$ 31,84 de R$ 32,00`) e nao tem titulo de saldo. Os 6 tambem acendem o selo, por
 * decisao do Ruan em 01/09: mostrar o que ENTROU e' honesto nos dois casos, e esconder a
 * diferenca seria justamente o defeito que este ADR conserta.
 */
export function ehBaixaParcial(t: TituloBaixavel): boolean {
  if (t.status !== 'pago') return false;
  if (t.valor_pago == null) return false;
  return centavos(t.valor_pago) < centavos(t.valor);
}

/**
 * O titulo NASCEU do que faltou noutro (`valor_original` maior que o proprio valor).
 *
 * ⚠️ **Medido nos DOIS sentidos antes de confiar no campo** (01/09/2026, prod): dos titulos com
 * `valor_original > valor`, **zero** estao sem a observacao de saldo parcial; e dos que tem a
 * observacao, **zero** estao sem a marca. Tambem **zero** tem `valor_original` MENOR que o valor.
 * Ou seja: o campo distingue sem falso positivo nem falso negativo — nao e' heuristica.
 */
export function ehTituloDeSaldo(t: TituloBaixavel): boolean {
  if (t.valor_original == null) return false;
  return centavos(t.valor_original) > centavos(t.valor);
}

/** Quanto ja tinha sido pago no titulo que originou este saldo. Zero quando nao e' saldo. */
export function jaPagoAntesDoSaldo(t: TituloBaixavel): number {
  if (!ehTituloDeSaldo(t)) return 0;
  return +(num(t.valor_original) - num(t.valor)).toFixed(2);
}

/**
 * A chave que junta os titulos de UM MESMO recibo.
 *
 * ⛔ NAO e' o dia: `data_pagamento` e' `date` pura, e o Ruan foi explicito — *"ele pode ate pagar
 * em duas vezes, de manha e a tarde, mais ai e' dois recibos"*. Tambem NAO e' a forma: era o
 * agrupamento antigo, e foi ele que partiu o recibo do EDS em dois (Pix num papel, Dinheiro no
 * outro) quando o pagamento era um so.
 *
 * ✅ E' o INSTANTE da baixa, sem janela de tolerancia nenhuma: os titulos de um mesmo lote sao
 * gravados na MESMA transacao e compartilham o `quando` ao microssegundo. Medido em 01/09/2026:
 * os 4 titulos do EDS caem em exatamente 2 grupos (13:40:36.072505 e 13:52:13.275823), e a trilha
 * cobre 367 de 367 titulos pagos.
 *
 * ⚠️ O `data_pagamento` fica como ultimo recurso pra titulo sem trilha (hoje nao ha nenhum) — sem
 * ele, um titulo assim viraria um grupo `null` que junta com qualquer outro sem trilha.
 */
export function chaveDoRecibo(t: { cliente_id: number; baixado_em?: string | null; data_pagamento?: string | null }): string {
  const instante = t.baixado_em ?? `dia:${t.data_pagamento ?? 'sem-data'}`;
  return `${t.cliente_id}|${instante}`;
}
